import { Form, Modal, ModalProps } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getActivePinCode } from "../services/pinService";

// Type declarations for Android WebView bridge
declare global {
  interface Window {
    Android?: {
      finish?: () => void;
      finishAffinity?: () => void;
      exitApp?: () => void;
      sendBroadcast?: (action: string) => void;
      moveTaskToBack?: (nonRoot: boolean) => void;
      closeCustomTab?: () => void;
      exitKiosk?: () => void;
      // Enhanced Android methods
      finishActivity?: () => void;
      finishAndRemoveTask?: () => void;
      finishAfterTransition?: () => void;
      onBackPressed?: () => void;
      systemExit?: () => void;
      homeButton?: () => void;
      minimizeApp?: () => void;
      closeApp?: () => void;
    };
    // Additional Android interfaces
    webkit?: {
      messageHandlers?: {
        android?: {
          postMessage?: (message: any) => void;
        };
        closeApp?: {
          postMessage?: (message: any) => void;
        };
        finish?: {
          postMessage?: (message: any) => void;
        };
        exitApp?: {
          postMessage?: (message: any) => void;
        };
      };
    };
    // Android PWA specific
    android?: {
      app?: {
        exit?: () => void;
        close?: () => void;
        finish?: () => void;
        minimize?: () => void;
      };
    };
  }
}

interface OTPModalProps extends ModalProps {
  onHide: () => void;
  onSuccess?: () => void; // Optional callback when PIN is verified successfully
}

const OTPModal = (props: OTPModalProps) => {
  const navigate = useNavigate();
  const currentRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState(Array(6).fill(""));
  const [pinError, setPinError] = useState("");
  const [correctPin, setCorrectPin] = useState<string | null>(null);
  const [isLoadingPin, setIsLoadingPin] = useState(false);

  // Fetch PIN code when modal opens
  useEffect(() => {
    if (props.show) {
      setIsLoadingPin(true);
      setPinError("");
      setCurrentPin(Array(6).fill(""));

      getActivePinCode()
        .then((result) => {
          if (result.success && result.pinCode) {
            setCorrectPin(result.pinCode);
          } else {
            setPinError(result.error || "Failed to load PIN code. Please try again.");
            console.error("Failed to fetch PIN code:", result.error);
          }
        })
        .catch((error) => {
          setPinError("Network error. Please check your connection and try again.");
          console.error("Error fetching PIN code:", error);
        })
        .finally(() => {
          setIsLoadingPin(false);
          // Small delay to ensure modal is fully rendered before focusing
          setTimeout(() => {
            currentRefs.current[0]?.focus();
          }, 100);
        });
    } else {
      // Reset state when modal closes
      setCorrectPin(null);
      setCurrentPin(Array(6).fill(""));
      setPinError("");
    }
  }, [props.show]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    type: "current"
  ) => {
    const { value } = e.target;
    if (!/^\d*$/.test(value)) return;

    // Clear error when user starts typing
    if (pinError) {
      setPinError("");
    }

    if (type === "current") {
      const updated = [...currentPin];
      updated[index] = value.slice(-1);
      setCurrentPin(updated);

      if (value && index < currentRefs.current.length - 1) {
        currentRefs.current[index + 1]?.focus();
      }

      // Auto-submit when 6th digit is entered
      if (updated.every((d) => d !== "")) {
        setTimeout(() => {
          const enteredPin = updated.join("");

          // Check if PIN is loaded
          if (!correctPin) {
            setPinError("PIN code not loaded. Please wait...");
            setCurrentPin(Array(6).fill(""));
            // Try to refetch PIN
            getActivePinCode()
              .then((result) => {
                if (result.success && result.pinCode) {
                  setCorrectPin(result.pinCode);
                  // Retry verification with the entered PIN
                  if (enteredPin === result.pinCode) {
                    props.onHide();

                    // If custom success handler provided, use it; otherwise default to navigate home
                    if (props.onSuccess) {
                      props.onSuccess();
                    } else {
                      // Default behavior: navigate to home page
                      navigate("/");
                    }
                  } else {
                    setPinError("Incorrect PIN. Please try again.");
                    setCurrentPin(Array(6).fill(""));
                    setTimeout(() => {
                      currentRefs.current[0]?.focus();
                    }, 100);
                  }
                } else {
                  setPinError(result.error || "Failed to load PIN code. Please try again.");
                }
              })
              .catch((error) => {
                setPinError("Network error. Please check your connection and try again.");
                console.error("Error fetching PIN code:", error);
              });
            return;
          }

          if (enteredPin === correctPin) {
            // PIN verified successfully
            props.onHide();

            // If custom success handler provided, use it; otherwise default to navigate home
            if (props.onSuccess) {
              props.onSuccess();
            } else {
              // Default behavior: navigate to home page
              navigate("/");
            }
          } else {
            setPinError("Incorrect PIN. Please try again.");
            setCurrentPin(Array(6).fill(""));
            // Focus first input after error
            setTimeout(() => {
              currentRefs.current[0]?.focus();
            }, 100);
          }
        }, 100); // Small delay to ensure state is updated
      }
    }
  };
  useEffect(() => {
    const threshold = 150; // px difference to assume keyboard is open
    let initialHeight = window.innerHeight;

    const handleResize = () => {
      const diff = initialHeight - window.innerHeight;
      if (diff > threshold) {
        setKeyboardOpen(true);
      } else {
        setKeyboardOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
    type: "current"
  ) => {
    if (e.key === "Backspace") {
      if (type === "current" && !currentPin[index] && index > 0) {
        currentRefs.current[index - 1]?.focus();
      }
    }
  };

  // ✅ Enhanced Device Detection with Comprehensive Platform Support
  const detectDeviceType = () => {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
    const isKiosk = window.location.href.includes('kiosk') || window.location.href.includes('fullscreen');
    const isElectron = userAgent.includes('Electron');
    const isWindows = /Windows/i.test(userAgent);
    const isMac = /Macintosh|Mac OS X/i.test(userAgent);
    const isLinux = /Linux/i.test(userAgent);

    return {
      // Device Types
      isAndroid: /Android/i.test(userAgent),
      isIOS: /iPhone|iPad|iPod/i.test(userAgent),
      isTablet: /iPad|Android.*Tablet|Tablet/i.test(userAgent),
      isPhone: isMobile && !/iPad|Android.*Tablet|Tablet/i.test(userAgent),

      // Desktop OS Types
      isWindows,
      isMac,
      isLinux,

      // App Types
      isPWA,
      isKiosk,
      isElectron,
      isWebView: /wv|WebView/i.test(userAgent),
      isChromeCustomTab: /Chrome.*Mobile/i.test(userAgent) && isPWA,
      isTWA: /TWA/i.test(userAgent), // Trusted Web Activity

      // Browser Types
      isChrome: /Chrome/i.test(userAgent),
      isFirefox: /Firefox/i.test(userAgent),
      isSafari: /Safari/i.test(userAgent) && !/Chrome/i.test(userAgent),
      isEdge: /Edg/i.test(userAgent),
      isOpera: /Opera|OPR/i.test(userAgent),
      isBrave: /Brave/i.test(userAgent),

      // Kiosk Detection
      isAndroidKiosk: /Android/i.test(userAgent) && (isKiosk || /Tablet|Kiosk/i.test(userAgent)),
      isIOSKiosk: /iPhone|iPad/i.test(userAgent) && isKiosk,
      isWindowsKiosk: isWindows && isKiosk,

      // Combined flags
      isMobile,
      isDesktop: !isMobile,

      // Platform-specific flags
      isAndroidChrome: /Android.*Chrome/i.test(userAgent),
      isIOSChrome: /iPhone|iPad.*CriOS/i.test(userAgent),
      isAndroidFirefox: /Android.*Firefox/i.test(userAgent),
      isIOSFirefox: /iPhone|iPad.*FxiOS/i.test(userAgent),
      isSamsungInternet: /SamsungBrowser/i.test(userAgent),
      isUCBrowser: /UCBrowser/i.test(userAgent)
    };
  };

  // ✅ Enhanced Android Methods - Optimized for Demo Success
  const getAndroidMethods = (deviceInfo: DeviceInfo) => [
    // CRITICAL: Primary Android WebView Bridge Methods (Most Reliable)
    {
      name: 'Android finish() - PRIMARY', method: () => {
        try {
          if (window.Android && typeof window.Android.finish === 'function') {
            window.Android.finish();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android finishAffinity() - PRIMARY', method: () => {
        try {
          if (window.Android && typeof window.Android.finishAffinity === 'function') {
            window.Android.finishAffinity();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android exitApp() - PRIMARY', method: () => {
        try {
          if (window.Android && typeof window.Android.exitApp === 'function') {
            window.Android.exitApp();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },

    // Enhanced Android Activity Methods
    {
      name: 'Android finishActivity()', method: () => {
        try {
          if (window.Android && typeof window.Android.finishActivity === 'function') {
            window.Android.finishActivity();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android finishAndRemoveTask()', method: () => {
        try {
          if (window.Android && typeof window.Android.finishAndRemoveTask === 'function') {
            window.Android.finishAndRemoveTask();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android finishAfterTransition()', method: () => {
        try {
          if (window.Android && typeof window.Android.finishAfterTransition === 'function') {
            window.Android.finishAfterTransition();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },

    // Android System and Intent Methods
    {
      name: 'Android moveTaskToBack()', method: () => {
        try {
          if (window.Android && typeof window.Android.moveTaskToBack === 'function') {
            window.Android.moveTaskToBack(true);
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android homeButton()', method: () => {
        try {
          if (window.Android && typeof window.Android.homeButton === 'function') {
            window.Android.homeButton();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android minimizeApp()', method: () => {
        try {
          if (window.Android && typeof window.Android.minimizeApp === 'function') {
            window.Android.minimizeApp();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android closeApp()', method: () => {
        try {
          if (window.Android && typeof window.Android.closeApp === 'function') {
            window.Android.closeApp();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },

    // Android WebKit Message Handlers
    {
      name: 'Android WebKit closeApp', method: () => {
        try {
          if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.closeApp) {
            window.webkit.messageHandlers.closeApp.postMessage?.({ action: 'close' });
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android WebKit finish', method: () => {
        try {
          if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.finish) {
            window.webkit.messageHandlers.finish.postMessage?.({ action: 'finish' });
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android WebKit exitApp', method: () => {
        try {
          if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.exitApp) {
            window.webkit.messageHandlers.exitApp.postMessage?.({ action: 'exit' });
            return true;
          }
        } catch (e) { }
        return false;
      }
    },

    // Android PWA and App Specific
    {
      name: 'Android PWA Exit', method: () => {
        try {
          if ((window.navigator as any).app && typeof (window.navigator as any).app.exitApp === 'function') {
            (window.navigator as any).app.exitApp();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'Android App Interface', method: () => {
        try {
          if (window.android && window.android.app && typeof window.android.app.exit === 'function') {
            window.android.app.exit();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },

    // Chrome Custom Tabs and TWA
    {
      name: 'Chrome Custom Tab Close', method: () => {
        try {
          if (window.Android && typeof window.Android.closeCustomTab === 'function') {
            window.Android.closeCustomTab();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },
    {
      name: 'TWA Close', method: () => {
        try {
          if ((window as any).twaAPI && typeof (window as any).twaAPI.close === 'function') {
            (window as any).twaAPI.close();
            return true;
          }
        } catch (e) { }
        return false;
      }
    },

    // Android Browser Specific
    ...(deviceInfo.isAndroidChrome ? [
      {
        name: 'Android Chrome Close', method: () => {
          try {
            if ((window as any).chrome && (window as any).chrome.app && typeof (window as any).chrome.app.close === 'function') {
              (window as any).chrome.app.close();
              return true;
            }
          } catch (e) { }
          return false;
        }
      }
    ] : []),
    ...(deviceInfo.isSamsungInternet ? [
      {
        name: 'Samsung Internet Close', method: () => {
          try {
            if ((window as any).samsung && (window as any).samsung.app && typeof (window as any).samsung.app.close === 'function') {
              (window as any).samsung.app.close();
              return true;
            }
          } catch (e) { }
          return false;
        }
      }
    ] : []),
    ...(deviceInfo.isUCBrowser ? [
      {
        name: 'UC Browser Close', method: () => {
          try {
            if ((window as any).ucbrowser && (window as any).ucbrowser.app && typeof (window as any).ucbrowser.app.close === 'function') {
              (window as any).ucbrowser.app.close();
              return true;
            }
          } catch (e) { }
          return false;
        }
      }
    ] : []),

    // Android Kiosk
    ...(deviceInfo.isAndroidKiosk ? [
      {
        name: 'Android Kiosk Exit', method: () => {
          try {
            if (window.Android && typeof window.Android.exitKiosk === 'function') {
              window.Android.exitKiosk();
              return true;
            }
          } catch (e) { }
          return false;
        }
      },
      {
        name: 'Android Kiosk API Close', method: () => {
          try {
            if ((window as any).kioskAPI && typeof (window as any).kioskAPI.close === 'function') {
              (window as any).kioskAPI.close();
              return true;
            }
          } catch (e) { }
          return false;
        }
      },
      {
        name: 'Android Kiosk System Exit', method: () => {
          try {
            if ((window as any).kioskAPI && typeof (window as any).kioskAPI.systemExit === 'function') {
              (window as any).kioskAPI.systemExit();
              return true;
            }
          } catch (e) { }
          return false;
        }
      }
    ] : [])
  ];

  const getIOSMethods = (deviceInfo: DeviceInfo) => [
    // iOS PWA and WebView
    { name: 'iOS PWA Exit', method: () => (window.navigator as any).app?.exitApp?.() },
    { name: 'iOS PWA Close', method: () => (window.navigator as any).app?.close?.() },
    { name: 'iOS WebView Close', method: () => (window as any).webkit?.messageHandlers?.closeApp?.postMessage?.() },

    // iOS Browser Specific
    ...(deviceInfo.isIOSChrome ? [
      { name: 'iOS Chrome Close', method: () => (window as any).chrome?.app?.close?.() }
    ] : []),
    ...(deviceInfo.isIOSFirefox ? [
      { name: 'iOS Firefox Close', method: () => (window as any).firefox?.app?.close?.() }
    ] : []),

    // iOS Kiosk
    ...(deviceInfo.isIOSKiosk ? [
      { name: 'iOS Kiosk Exit', method: () => (window as any).kioskAPI?.close?.() },
      { name: 'iOS Kiosk System Exit', method: () => (window as any).kioskAPI?.systemExit?.() }
    ] : []),

    // iOS Safari and WebKit (limited options)
    { name: 'iOS Safari Redirect', method: () => { window.location.href = 'about:blank'; } },
    { name: 'iOS WebKit Close', method: () => (window as any).webkit?.messageHandlers?.close?.postMessage?.() },
    {
      name: 'iOS Home Button Sim', method: () => {
        // Simulate home button press by minimizing
        if (window.navigator && (window.navigator as any).standalone) {
          window.location.href = 'about:blank';
        }
      }
    }
  ];

  const getElectronMethods = () => [
    { name: 'Electron Close App', method: () => (window as any).electronAPI?.closeApp?.() },
    { name: 'Electron Window Close', method: () => (window as any).electronAPI?.closeWindow?.() },
    { name: 'Electron App Quit', method: () => (window as any).electronAPI?.quit?.() },
    { name: 'Electron IPC Close', method: () => (window as any).electronAPI?.ipcRenderer?.send?.('close-app') }
  ];

  const getDesktopMethods = (deviceInfo: DeviceInfo) => [
    // Windows Desktop
    ...(deviceInfo.isWindows ? [
      { name: 'Windows App Close', method: () => (window as any).windows?.app?.close?.() },
      { name: 'Windows System Exit', method: () => (window as any).windows?.system?.exit?.() }
    ] : []),

    // Mac Desktop
    ...(deviceInfo.isMac ? [
      { name: 'Mac App Close', method: () => (window as any).mac?.app?.close?.() },
      { name: 'Mac System Exit', method: () => (window as any).mac?.system?.exit?.() }
    ] : []),

    // Linux Desktop
    ...(deviceInfo.isLinux ? [
      { name: 'Linux App Close', method: () => (window as any).linux?.app?.close?.() },
      { name: 'Linux System Exit', method: () => (window as any).linux?.system?.exit?.() }
    ] : [])
  ];

  const getBrowserMethods = (deviceInfo: DeviceInfo) => [
    // Chrome Browser
    ...(deviceInfo.isChrome ? [
      { name: 'Chrome Extension Close', method: () => (window as any).chrome?.app?.close?.() },
      { name: 'Chrome Extension Exit', method: () => (window as any).chrome?.app?.exit?.() }
    ] : []),

    // Firefox Browser
    ...(deviceInfo.isFirefox ? [
      { name: 'Firefox Extension Close', method: () => (window as any).firefox?.app?.close?.() },
      { name: 'Firefox Extension Exit', method: () => (window as any).firefox?.app?.exit?.() }
    ] : []),

    // Safari Browser
    ...(deviceInfo.isSafari ? [
      { name: 'Safari Extension Close', method: () => (window as any).safari?.app?.close?.() }
    ] : []),

    // Edge Browser
    ...(deviceInfo.isEdge ? [
      { name: 'Edge Extension Close', method: () => (window as any).edge?.app?.close?.() }
    ] : []),

    // Opera Browser
    ...(deviceInfo.isOpera ? [
      { name: 'Opera Extension Close', method: () => (window as any).opera?.app?.close?.() }
    ] : []),

    // Brave Browser
    ...(deviceInfo.isBrave ? [
      { name: 'Brave Extension Close', method: () => (window as any).brave?.app?.close?.() }
    ] : [])
  ];

  const getKioskMethods = (deviceInfo: DeviceInfo) => [
    { name: 'Kiosk API Close', method: () => (window as any).kioskAPI?.close?.() },
    { name: 'Kiosk Exit', method: () => (window as any).kioskAPI?.exit?.() },
    { name: 'Kiosk System Exit', method: () => (window as any).kioskAPI?.systemExit?.() },
    { name: 'Kiosk Force Close', method: () => (window as any).kioskAPI?.forceClose?.() },

    // Platform-specific kiosk methods
    ...(deviceInfo.isWindowsKiosk ? [
      { name: 'Windows Kiosk Exit', method: () => (window as any).windowsKiosk?.exit?.() }
    ] : []),
    ...(deviceInfo.isAndroidKiosk ? [
      { name: 'Android Kiosk Exit', method: () => (window as any).androidKiosk?.exit?.() }
    ] : []),
    ...(deviceInfo.isIOSKiosk ? [
      { name: 'iOS Kiosk Exit', method: () => (window as any).iosKiosk?.exit?.() }
    ] : [])
  ];

  const getFallbackMethods = () => [
    // Standard web methods
    { name: 'Window Close', method: () => window.close() },
    { name: 'Location Redirect Blank', method: () => { window.location.href = 'about:blank'; } },
    { name: 'Location Redirect Data', method: () => { window.location.href = 'data:text/html,<html><body>App Closed</body></html>'; } },
    { name: 'History Back', method: () => window.history.back() },

    // Advanced fallback methods
    {
      name: 'Document Write Close', method: () => {
        document.write('<html><body><h1>App Closed</h1></body></html>');
        document.close();
      }
    },
    {
      name: 'Body Replace Close', method: () => {
        document.body.innerHTML = '<div style="text-align:center;margin-top:50px;"><h1>App Closed</h1></div>';
      }
    },

    // Final attempts
    {
      name: 'Final Redirect with Delay', method: () => {
        setTimeout(() => { window.location.href = 'about:blank'; }, 1000);
      }
    },
    {
      name: 'Ultimate Fallback', method: () => {
        setTimeout(() => {
          try {
            window.location.replace('about:blank');
          } catch (e) {
            // Last resort - do nothing
          }
        }, 2000);
      }
    }
  ];

  // ✅ Enhanced Safe Method Execution with Comprehensive Error Handling
  const executeMethodsSafely = (methods: Array<{ name: string, method: () => any }>) => {
    let methodIndex = 0;
    let successCount = 0;
    const maxSuccessCount = 3; // Stop after 3 successful attempts

    const tryNextMethod = () => {
      if (methodIndex >= methods.length || successCount >= maxSuccessCount) {
        // All methods exhausted or enough successes achieved
        return;
      }

      const currentMethod = methods[methodIndex];
      methodIndex++;

      try {
        // Try the method with timeout
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Method timeout')), 5000);
        });

        const methodPromise = Promise.resolve(currentMethod.method());

        Promise.race([methodPromise, timeoutPromise])
          .then((result) => {
            // Method succeeded
            successCount++;

            // For critical methods, don't try others
            if (currentMethod.name.includes('finish') ||
              currentMethod.name.includes('exitApp') ||
              currentMethod.name.includes('closeApp')) {
              return; // Stop here
            }

            // For other methods, continue trying
            setTimeout(tryNextMethod, 100);
          })
          .catch((error) => {
            // Method failed or timed out, try next
            setTimeout(tryNextMethod, 50);
          });

      } catch (error) {
        // Synchronous method failed, try next
        setTimeout(tryNextMethod, 50);
      }
    };

    // Start trying methods
    tryNextMethod();
  };

  // ✅ Enhanced Method Collection and Execution - Android Priority for Demo
  const executeClosingMethods = (deviceInfo: DeviceInfo) => {
    const methods: Array<{ name: string, method: () => any }> = [];

    // Build method list based on device type (ordered by priority)

    // CRITICAL: Android gets highest priority for demo
    if (deviceInfo.isAndroid) {
      methods.push(...getAndroidMethods(deviceInfo));
    }

    // 1. Platform-specific methods (highest priority)
    if (deviceInfo.isElectron) {
      methods.push(...getElectronMethods());
    }

    if (deviceInfo.isIOS) {
      methods.push(...getIOSMethods(deviceInfo));
    }

    // 2. Desktop methods
    if (deviceInfo.isDesktop) {
      methods.push(...getDesktopMethods(deviceInfo));
    }

    // 3. Browser-specific methods
    methods.push(...getBrowserMethods(deviceInfo));

    // 4. Kiosk methods (if applicable)
    if (deviceInfo.isKiosk) {
      methods.push(...getKioskMethods(deviceInfo));
    }

    // 5. Always add fallback methods (lowest priority)
    methods.push(...getFallbackMethods());

    // Execute each method safely with comprehensive error handling
    executeMethodsSafely(methods);
  };

  // ✅ Android-Specific Test Function for Demo
  const testAndroidBridge = () => {
    const androidTests = [
      { name: 'window.Android exists', test: () => typeof window.Android !== 'undefined' },
      { name: 'window.Android.finish exists', test: () => typeof window.Android?.finish === 'function' },
      { name: 'window.Android.finishAffinity exists', test: () => typeof window.Android?.finishAffinity === 'function' },
      { name: 'window.Android.exitApp exists', test: () => typeof window.Android?.exitApp === 'function' },
      { name: 'window.webkit exists', test: () => typeof window.webkit !== 'undefined' },
      { name: 'PWA mode detected', test: () => window.matchMedia('(display-mode: standalone)').matches },
      { name: 'Android user agent', test: () => /Android/i.test(navigator.userAgent) }
    ];

    const results = androidTests.map(test => ({
      name: test.name,
      result: test.test()
    }));

    // Log results for debugging
    return results;
  };

  // Device info interface for type safety
  interface DeviceInfo {
    isAndroid: boolean;
    isIOS: boolean;
    isTablet: boolean;
    isPhone: boolean;
    isWindows: boolean;
    isMac: boolean;
    isLinux: boolean;
    isPWA: boolean;
    isKiosk: boolean;
    isElectron: boolean;
    isWebView: boolean;
    isChromeCustomTab: boolean;
    isTWA: boolean;
    isChrome: boolean;
    isFirefox: boolean;
    isSafari: boolean;
    isEdge: boolean;
    isOpera: boolean;
    isBrave: boolean;
    isAndroidKiosk: boolean;
    isIOSKiosk: boolean;
    isWindowsKiosk: boolean;
    isMobile: boolean;
    isDesktop: boolean;
    isAndroidChrome: boolean;
    isIOSChrome: boolean;
    isAndroidFirefox: boolean;
    isIOSFirefox: boolean;
    isSamsungInternet: boolean;
    isUCBrowser: boolean;
  }

  // ✅ Universal App Closing System with Comprehensive Error Handling - Android Optimized
  const closePWA = () => {
    try {
      // Test Android bridge for debugging (only in development)
      if (process.env.NODE_ENV === 'development') {
        testAndroidBridge();
      }

      // Detect device type with error handling
      let deviceInfo: DeviceInfo;
      try {
        deviceInfo = detectDeviceType();
      } catch (detectionError) {
        // If detection fails, use basic fallback
        deviceInfo = {
          isMobile: true,
          isDesktop: false,
          isAndroid: /Android/i.test(navigator.userAgent),
          isIOS: false,
          isTablet: false,
          isPhone: false,
          isWindows: false,
          isMac: false,
          isLinux: false,
          isPWA: false,
          isKiosk: false,
          isElectron: false,
          isWebView: false,
          isChromeCustomTab: false,
          isTWA: false,
          isChrome: false,
          isFirefox: false,
          isSafari: false,
          isEdge: false,
          isOpera: false,
          isBrave: false,
          isAndroidKiosk: false,
          isIOSKiosk: false,
          isWindowsKiosk: false,
          isAndroidChrome: false,
          isIOSChrome: false,
          isAndroidFirefox: false,
          isIOSFirefox: false,
          isSamsungInternet: false,
          isUCBrowser: false
        };
      }

      // CRITICAL: For Android, try ONLY actual app closing methods - NO about:blank
      if (deviceInfo.isAndroid) {
        // Try ONLY Android app closing methods - NO navigation or page clearing
        const androidCloseMethods = [
          // Primary Android WebView methods (most reliable)
          () => {
            try {
              if (window.Android?.finish) {
                window.Android.finish();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.finishAffinity) {
                window.Android.finishAffinity();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.exitApp) {
                window.Android.exitApp();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.finishActivity) {
                window.Android.finishActivity();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.finishAndRemoveTask) {
                window.Android.finishAndRemoveTask();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.moveTaskToBack) {
                window.Android.moveTaskToBack(true);
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.homeButton) {
                window.Android.homeButton();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.minimizeApp) {
                window.Android.minimizeApp();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.closeApp) {
                window.Android.closeApp();
                return true;
              }
            } catch (e) { }
            return false;
          },

          // PWA specific methods (only if they exist)
          () => {
            try {
              if ((window.navigator as any).app?.exitApp) {
                (window.navigator as any).app.exitApp();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if ((window.navigator as any).app?.close) {
                (window.navigator as any).app.close();
                return true;
              }
            } catch (e) { }
            return false;
          },

          // Alternative Android app interface
          () => {
            try {
              if (window.android?.app?.exit) {
                window.android.app.exit();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.android?.app?.close) {
                window.android.app.close();
                return true;
              }
            } catch (e) { }
            return false;
          },

          // WebKit message handlers
          () => {
            try {
              if (window.webkit?.messageHandlers?.closeApp?.postMessage) {
                window.webkit.messageHandlers.closeApp.postMessage({ action: 'close' });
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.webkit?.messageHandlers?.finish?.postMessage) {
                window.webkit.messageHandlers.finish.postMessage({ action: 'finish' });
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.webkit?.messageHandlers?.exitApp?.postMessage) {
                window.webkit.messageHandlers.exitApp.postMessage({ action: 'exit' });
                return true;
              }
            } catch (e) { }
            return false;
          },

          // Browser specific (only if they exist)
          () => {
            try {
              if ((window as any).chrome?.app?.close) {
                (window as any).chrome.app.close();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if ((window as any).twaAPI?.close) {
                (window as any).twaAPI.close();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.closeCustomTab) {
                window.Android.closeCustomTab();
                return true;
              }
            } catch (e) { }
            return false;
          },

          // Kiosk methods (only if they exist)
          () => {
            try {
              if ((window as any).kioskAPI?.close) {
                (window as any).kioskAPI.close();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if ((window as any).kioskAPI?.systemExit) {
                (window as any).kioskAPI.systemExit();
                return true;
              }
            } catch (e) { }
            return false;
          },
          () => {
            try {
              if (window.Android?.exitKiosk) {
                window.Android.exitKiosk();
                return true;
              }
            } catch (e) { }
            return false;
          }
        ];

        // Execute Android methods with success detection
        let successCount = 0;
        const maxAttempts = 3;

        androidCloseMethods.forEach((method, index) => {
          setTimeout(() => {
            try {
              const success = method();
              if (success) {
                successCount++;
                // If we get a successful close, stop trying other methods
                if (successCount >= maxAttempts) {
                  return;
                }
              }
            } catch (e) {
              // Silent fail for each method
            }
          }, index * 100); // Execute every 100ms for better reliability
        });

        // Show success message AFTER attempting to close
        setTimeout(() => {
          alert("PIN verified successfully! App closing...");
        }, 200);

        return; // Exit early for Android
      }

      // For Windows PWA and other platforms
      if (deviceInfo.isPWA || deviceInfo.isWindows) {
        // Try Windows PWA specific methods
        const windowsCloseMethods = [
          () => window.close(),
          () => (window.navigator as any).app?.exitApp?.(),
          () => (window as any).electronAPI?.close?.(),
          () => window.location.href = 'about:blank',
          () => window.location.replace('about:blank'),
          () => {
            try {
              document.body.innerHTML = '<div style="text-align:center;margin-top:50px;font-size:24px;"><h1>App Closed Successfully</h1></div>';
            } catch (e) { }
          }
        ];

        windowsCloseMethods.forEach((method, index) => {
          setTimeout(() => {
            try {
              method();
            } catch (e) { }
          }, index * 100);
        });

        setTimeout(() => {
          alert("PIN verified successfully! App closing...");
        }, 100);

        return;
      }

      // Execute closing methods with comprehensive error handling for other platforms
      try {
        executeClosingMethods(deviceInfo);
      } catch (executionError) {
        // If execution fails, try direct fallback methods
        setTimeout(() => {
          try {
            window.location.href = 'about:blank';
          } catch (e) {
            // Silent fail
          }
        }, 1000);
      }

    } catch (error) {
      // Ultimate fallback - multiple attempts with increasing delays
      const fallbackAttempts = [
        () => window.location.href = 'about:blank',
        () => window.close(),
        () => window.location.replace('about:blank'),
        () => {
          document.body.innerHTML = '<div style="text-align:center;margin-top:50px;"><h1>App Closed</h1></div>';
        }
      ];

      fallbackAttempts.forEach((attempt, index) => {
        setTimeout(() => {
          try {
            attempt();
          } catch (e) {
            // Silent fail for each attempt
          }
        }, (index + 1) * 500);
      });
    }
  };

  return (
    <Modal
      {...props}
      backdrop="static"
      className={`otpModal ${keyboardOpen ? "keyboard-open" : ""}`}
      keyboard={false}
      scrollable={false}
    >
      <Modal.Body>
        <div className="otpModalBody" style={{ marginTop: "-25%" }}>
          <div>
            <button className="back p-1" onClick={() => props.onHide()}>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.52" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x-icon lucide-x"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </button>
          </div>
          <div className="header">Enter your PIN code to access Admin Mode</div>
          {isLoadingPin ? (
            <div className="text-center mt-3">
              <div style={{ fontSize: '13px', fontWeight: '500', color: '#666' }}>
                Loading PIN code...
              </div>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-center gap-3">
                {currentPin.map((digit, index) => (
                  <Form.Control
                    key={index}
                    ref={(el: any) => (currentRefs.current[index] = el)}
                    value={digit}
                    onChange={(e: any) => handleChange(e, index, "current")}
                    onKeyDown={(e: any) => handleKeyDown(e, index, "current")}
                    maxLength={1}
                    placeholder=""
                    className="px-1 text-center"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={isLoadingPin || !correctPin}
                    autoComplete="off"
                  />
                ))}
              </div>
              {pinError && (
                <div className="text-center mt-3">
                  <div className="text-danger" style={{ fontSize: '13px', fontWeight: '500' }}>
                    {pinError}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default OTPModal;
